/**
 * The local folder check — is every invoice image in the folder Foundation
 * opens it from (the "local path of documents folder" in Settings, e.g.
 * V:\Invoices)?
 *
 * Beiing Human's servers cannot see a customer's computer, so the check runs
 * HERE, in the browser, on the computer that runs Foundation. The user picks the
 * folder once (File System Access API, Chrome / Edge); the handle is kept in
 * IndexedDB, and from then on the app scans the folder in the background
 * whenever it is open on that computer, at most every SCAN_INTERVAL_HOURS. The
 * folder is only READ — never written to: it is the Google Drive for Desktop
 * mirror, and anything written into it would be uploaded back to Drive.
 *
 * The answer goes to Tier B (/api/v1/file-sync/local-report), which stores it on
 * each document and emails support about anything newly missing.
 */

export const SCAN_INTERVAL_HOURS = 12;
const REPORT_BATCH = 2000;
const MANIFEST_PAGE = 1000;
const DB_NAME = 'bh-file-sync';
const STORE = 'folders';
const LAST_SCAN_KEY = (companyId) => `bhFileSyncLastScan:${companyId}`;

export const isFolderAccessSupported = () =>
  typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function';

// ---- IndexedDB (a directory handle survives a reload only there)

const openDb = () =>
  new Promise((resolve, reject) => {
    const req = window.indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const idb = async (mode, fn) => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const req = fn(tx.objectStore(STORE));
    tx.oncomplete = () => resolve(req?.result);
    tx.onerror = () => reject(tx.error);
  });
};

export const saveFolderHandle = (companyId, handle) =>
  idb('readwrite', (s) => s.put(handle, String(companyId)));

export const loadFolderHandle = async (companyId) => {
  try {
    return (await idb('readonly', (s) => s.get(String(companyId)))) || null;
  } catch {
    return null;
  }
};

export const forgetFolderHandle = (companyId) =>
  idb('readwrite', (s) => s.delete(String(companyId)));

/**
 * Is this browser grant for the folder the saved local path names? The saved
 * path is the ONLY folder the check may read — a grant for anything else (a
 * folder allowed before the path changed, or picked by mistake) is not used.
 * Compared by name: a browser never reveals a folder's full path.
 */
export const isHandleForPath = (handle, savedPath) => {
  const expected = folderNameOf(savedPath);
  return !!handle?.name && !!expected && handle.name.toLowerCase() === expected.toLowerCase();
};

/**
 * Asks the browser for access to the folder the company already named in
 * Settings ("Add local path of documents folder"). A website cannot open a
 * folder from a typed path — browsers only grant one the user picks — so this
 * opens the picker once, and REFUSES any folder other than the one the path
 * names (compared by name: the browser never reveals a full path). Must be
 * called from a click.
 */
export const PATH_MISMATCH_MESSAGE =
  'The given path does not match a folder on your local machine.';

export const chooseFolder = async (companyId, expectedPath) => {
  const handle = await window.showDirectoryPicker({ id: 'bh-foundation-folder', mode: 'read' });
  const expected = folderNameOf(expectedPath);
  if (expected && handle.name.toLowerCase() !== expected.toLowerCase()) {
    const error = new Error(
      `${PATH_MISMATCH_MESSAGE} The saved path is "${expectedPath}", but the folder selected was "${handle.name}".`
    );
    error.name = 'WrongFolderError';
    throw error;
  }
  await saveFolderHandle(companyId, handle);
  return handle;
};

/** Can the folder actually be opened right now (still there, not moved)? */
export const folderIsReadable = async (handle) => {
  try {
    await handle.entries().next();
    return true;
  } catch {
    return false;
  }
};

/** 'granted' | 'prompt' | 'denied' — without asking. */
export const folderPermission = async (handle) => {
  try {
    return (await handle.queryPermission({ mode: 'read' })) || 'prompt';
  } catch {
    return 'prompt';
  }
};

/** Asks again (the browser forgets after a restart). Must be called from a click. */
export const requestFolderPermission = async (handle) => {
  try {
    return (await handle.requestPermission({ mode: 'read' })) === 'granted';
  } catch {
    return false;
  }
};

// ---- the check

export const lastScanAt = (companyId) => {
  try {
    const v = window.localStorage.getItem(LAST_SCAN_KEY(companyId));
    return v ? new Date(v) : null;
  } catch {
    return null;
  }
};

const setLastScanAt = (companyId, date) => {
  try {
    window.localStorage.setItem(LAST_SCAN_KEY(companyId), date.toISOString());
  } catch {
    /* a check that ran is still a check that ran */
  }
};

export const scanIsDue = (companyId, now = new Date()) => {
  const last = lastScanAt(companyId);
  return !last || now.getTime() - last.getTime() >= SCAN_INTERVAL_HOURS * 3600 * 1000;
};

/**
 * The folder is flat — Beiing Human uploads every file into one Drive folder —
 * so only its top level is read. Lower-cased: Windows file names are not case
 * sensitive, so neither is Foundation's link.
 */
export const listFolderNames = async (handle) => {
  const names = new Set();
  for await (const [name, entry] of handle.entries()) {
    if (entry.kind === 'file') names.add(name.toLowerCase());
  }
  return names;
};

/** Pure: which expected files are in the folder. */
export const compareFolderToManifest = (names, files) =>
  files
    .filter((f) => f?.fileName)
    .map((f) => ({
      documentId: f.documentId,
      present: names.has(String(f.fileName).toLowerCase()) ? 'present' : 'missing'
    }));

/** Pure: the last segment of a Windows or POSIX path ("V:\\Invoices" → "Invoices"). */
export const folderNameOf = (path) =>
  String(path || '')
    .trim()
    .replace(/[\\/]+$/, '')
    .split(/[\\/]/)
    .pop() || '';

export const describeThisComputer = () => {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : 'Browser';
  const os = /Windows/.test(ua) ? 'Windows' : /Mac OS X/.test(ua) ? 'Mac' : 'computer';
  return `${browser} on ${os}`;
};

/**
 * Runs one check: fetch every expected file name, read the folder, report.
 * `api` is injected ({getManifest, postReport}) so this can be tested without
 * a browser folder. Returns a summary; never throws for a folder it cannot
 * read — that is reported as an `error`, so no document is called missing
 * because the folder was simply not reachable.
 */
export const runLocalFolderCheck = async ({
  companyId,
  handle,
  savedPath,
  api,
  now = new Date()
}) => {
  const machine = describeThisComputer();
  const folderName = handle?.name || '';
  // Never check (or report on) any folder but the saved one.
  if (!isHandleForPath(handle, savedPath)) {
    return { checked: 0, missing: 0, skipped: true };
  }

  let names;
  try {
    names = await listFolderNames(handle);
  } catch (error) {
    const message = `The folder "${folderName}" could not be opened (${
      error?.name || 'error'
    }). It may have been moved, renamed, or Google Drive for Desktop may not be running.`;
    await api.postReport({ machine, folderName, error: message, final: true, results: [] });
    setLastScanAt(companyId, now);
    return { checked: 0, missing: 0, error: message };
  }

  const files = [];
  let folderPath = null;
  for (let page = 1; ; page += 1) {
    const data = await api.getManifest({ page, limit: MANIFEST_PAGE });
    folderPath = data?.folderPath ?? folderPath;
    files.push(...(data?.files || []));
    if (!data?.files?.length || files.length >= Number(data?.total || 0)) break;
  }

  const results = compareFolderToManifest(names, files);
  if (!results.length) {
    await api.postReport({ machine, folderName, final: true, results: [] });
  }
  for (let i = 0; i < results.length; i += REPORT_BATCH) {
    await api.postReport({
      machine,
      folderName,
      // The last batch is `final`: support gets ONE email for the run.
      final: i + REPORT_BATCH >= results.length,
      results: results.slice(i, i + REPORT_BATCH)
    });
  }
  setLastScanAt(companyId, now);
  return {
    checked: results.length,
    missing: results.filter((r) => r.present === 'missing').length,
    folderName,
    folderPath
  };
};
